// src/components/JobList.tsx
import React, { useEffect, useState } from 'react';
import { useAuth } from '../AuthContext';
import { useParams, Link } from 'react-router-dom';
import DownloadButton from './DownloadButton';
import { BASE_URI, prettyPrintDate } from '../Config';

interface DataRow {
    alpha_v: string | null;
    alpha_j: string | null;
    alpha_cdr3: string | null;
    beta_v: string | null;
    beta_j: string | null;
    beta_cdr3: string | null;
    antigen: string | null;
    mhc: string | null;
    score: string;
    pct_score: string;
}

interface JobMeta {
    filename: string,
    id: string, 
    target_mhc: string | null,
    target_antigen: string | null,
    file_type: string | null,
    status: string,
    created_at: string,
    failure_reason: string | null
}

function floatToString(value: number): string {
    if (Math.abs(value) >= 0.01 && Math.abs(value) < 1e7 || value === 0) {
        return value.toFixed(10).replace(/\.?0+$/, '');  // Remove trailing zeros
    } else {
        // Convert to exponential and remove trailing zeros after the decimal point
        const expStr = value.toExponential();
        const [base, exponent] = expStr.split('e');
        const cleanedBase = parseFloat(base).toString();  // Removes trailing zeros
        return `${cleanedBase}e${exponent}`;
    }
}

function getSortFunc(f: string): (a: DataRow, b: DataRow) => number {
    if (f === "pct"){
        return ((a: DataRow, b: DataRow) => {
            if (a.pct_score === "N/A" && b.pct_score !== "N/A" ) {
                return 1
            }
            if (a.pct_score !== "N/A" && b.pct_score === "N/A" ) {
                return -1
            }
            if (a.pct_score === "N/A" && b.pct_score === "N/A" ) {
                return 0
            }
            else {
                return parseFloat(a.pct_score) > parseFloat(b.pct_score) ? -1 : 1
            }
        })
    }
    else {
        return ((a: DataRow, b: DataRow) => {
            if (a.score === "N/A" && b.score !== "N/A" ) {
                return 1
            }
            if (a.score !== "N/A" && b.score === "N/A" ) {
                return -1
            }
            if (a.score === "N/A" && b.score === "N/A" ) {
                return 0
            }
            else {
                return parseFloat(a.score) > parseFloat(b.score) ? -1 : 1
            }
        })
    }
}

const Job: React.FC = () => {
    const [scoredPairs, setScoredPairs] = useState<DataRow[]>([]);
    const [jobMeta, setJobMeta] = useState<JobMeta | null>(null);
    const [sortField, setSortField] = useState<string>("raw");
    const [isLoading, setIsLoading] = useState(true);

    const { userToken } = useAuth();
    const { id } = useParams<{ id: string }>();

    useEffect(() => {
        
        // Mock API call
        const fetchJobs = async () => {
            // await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate network delay
            try {
                const response = await fetch(`${BASE_URI}/job_info/${id}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${userToken}`
                    }
                });
    
                const data = await response.json();

                if (data.job_meta) {
                    setJobMeta(data.job_meta)
                }
    
                if (data.scored_pairs) {
                    console.log("Retrieved jobs successful!");
                    console.log(data);
                    setScoredPairs(data.scored_pairs);
                    // Redirect or perform other actions after successful signup
                } else {
                    console.log("Retrieved jobs failed!");
                }
            } catch (error) {
                console.error("There was an error during retrieving jobs:", error);
            } finally {
                setIsLoading(false);  // Ensure loading state is turned off after fetching data or an error.
            }
            
        };

        fetchJobs();
    }, [userToken, id]);

    return (
        <div id="content-pane">
            {isLoading ? (
                <div className='spinner'></div>
            ) : (
                <>
                {jobMeta &&
                    <div className="job_info">
                        <h3>Job information</h3>
                        <div className="info_item">
                            <span className="info_field">File name:</span><span>{jobMeta.filename}</span>
                        </div>
                        <div className="info_item">
                            <span className="info_field">Created:</span><span>{prettyPrintDate(jobMeta.created_at)}</span>
                        </div>
                        <div className="info_item">
                            <span className="info_field">File type:</span><span>{jobMeta.file_type}</span>
                        </div>
                        <div className="info_item">
                            <span className="info_field">Target:</span><span>{jobMeta.target_antigen} {jobMeta.target_mhc}</span>
                        </div>
                        <div className="info_item">
                            <span className="info_field">Status:</span><span>{jobMeta.status}</span>
                        </div>
                        {jobMeta.status === "Succeeded" && <div className="info_item">
                            <span className="info_field">Download:</span><span><DownloadButton url_str={`jobs/${jobMeta.id}/download/output`} filename={"output_"+jobMeta.filename} text='Link'></DownloadButton></span>
                        </div>}
                    </div>
                }
                {jobMeta && jobMeta.failure_reason && <div className='job_failure_info'><p>{jobMeta.failure_reason}</p><p>For more information, see our <Link to="/tutorial">Tutorial</Link>.</p></div>}
                {scoredPairs.length > 0 && <div>
                    <h3>Scored TCRs</h3>
                    <table className="score_data">
                    <thead>
                        <tr>
                            <th>αV</th>
                            <th>αJ</th>
                            <th>αCDR3</th>
                            <th>βV</th>
                            <th>βJ</th>
                            <th>βCDR3</th>
                            <th>Peptide</th>
                            <th>MHC</th>
                            <th>Score</th>
                            <th>Percentile</th>
                        </tr>
                    </thead>
                    <tbody>
                        {scoredPairs.sort(getSortFunc(sortField)).map(job => (
                            <tr>
                                <td>{job.alpha_v && job.alpha_v}</td>
                                <td>{job.alpha_j && job.alpha_j}</td>
                                <td>{job.alpha_cdr3 && job.alpha_cdr3}</td>
                                <td>{job.beta_v && job.beta_v}</td>
                                <td>{job.beta_j && job.beta_j}</td>
                                <td>{job.beta_cdr3 && job.beta_cdr3}</td>
                                <td>{job.antigen}</td>
                                <td>{job.mhc}</td>
                                <td>{job.score && job.score !== "N/A" ? floatToString(parseFloat(job.score)) : "N/A"}</td>
                                <td>{job.pct_score && job.pct_score !== "N/A" ? floatToString(parseFloat(job.pct_score)) : "N/A"}</td>
                            </tr>
                        ))}
                    </tbody>
                </table></div>}
                </>
            )}
        </div>
    );
}

export default Job;
